import React from 'react';
import {
  Text,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular, Open20Regular } from '@fluentui/react-icons';
import type { DataModelView } from '../../api/dataModelsService';
import { semanticViewGrainDisplayName } from '../../utils/semanticViewDisplayNames';

const useStyles = makeStyles({
  card: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
      boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
    },
  },
  cardSelected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    borderLeftWidth: '3px',
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: '0 2px 8px rgba(0,90,220,0.12)',
  },
  row1: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
  },
  titleArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
    overflow: 'hidden',
  },
  row2: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  row3: {
    marginTop: '4px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  moreBtn: {
    opacity: 0,
    transition: 'opacity 0.15s',
  },
  cardHoverMore: {
    ':hover .more-btn': {
      opacity: 1,
    },
  },
});

function getViewTypeBadge(viewType: string): { label: string; color: 'success' | 'important' | 'informative' } {
  if (viewType === 'dimension') return { label: 'DIM', color: 'informative' };
  if (viewType === 'multi_fact') return { label: 'MULTI', color: 'important' };
  return { label: 'FACT', color: 'success' };
}

// @ts-ignore — reserved for future use
// function getStatusBadge(status: string): { label: string; color: 'success' | 'warning' | 'danger' | 'informative' } {
//   if (status === 'active') return { label: 'Generated', color: 'success' };
//   if (status === 'invalid') return { label: 'Error', color: 'danger' };
//   if (status === 'draft') return { label: 'Review', color: 'warning' };
//   return { label: status, color: 'informative' };
// }

interface SemanticViewCardProps {
  view: DataModelView;
  isSelected: boolean;
  onSelect: (viewId: string) => void;
}

const SemanticViewCard: React.FC<SemanticViewCardProps> = ({ view, isSelected, onSelect }) => {
  const styles = useStyles();
  const typeBadge = getViewTypeBadge(view.viewType);
  const grainLabel = semanticViewGrainDisplayName(view);

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
      onClick={() => onSelect(view.id)}
    >
      {/* Row 1: Title + type badge + more */}
      <div className={styles.row1}>
        <div className={styles.titleArea}>
          <Text weight="semibold" size={200} truncate wrap={false}>
            {view.title || view.name}
          </Text>
          <Badge appearance="filled" color={typeBadge.color} size="small">
            {typeBadge.label}
          </Badge>
        </div>
        <Menu>
          <MenuTrigger>
            <Button
              appearance="subtle"
              icon={<MoreHorizontal20Regular />}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Open20Regular />} onClick={() => onSelect(view.id)}>Open</MenuItem>
              <Tooltip content="Coming soon" relationship="description">
                <MenuItem disabled>Edit view</MenuItem>
              </Tooltip>
              <Tooltip content="Coming soon" relationship="description">
                <MenuItem disabled>Delete view</MenuItem>
              </Tooltip>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>

      {/* Row 2: grain + tables + columns */}
      <div className={styles.row2}>
        {grainLabel && (
          <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
            Grain: {grainLabel}
          </Text>
        )}
        {view.baseTables.length > 0 && (
          <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
            {view.baseTables.length} table{view.baseTables.length !== 1 ? 's' : ''}
          </Text>
        )}
        {Array.isArray(view.columnsJson) && view.columnsJson.length > 0 && (
          <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
            {view.columnsJson.length} columns
          </Text>
        )}
      </div>
    </div>
  );
};

export default SemanticViewCard;